import { authenticatedFetch, getFromGalaxyCDN, toQueryString } from '../api';
import { paths } from '../galaxyClient/types/Accounting';

export const getPaymentRequests = async () =>
  await authenticatedFetch<
    paths['/v2/providers/partner/payment-requests']['get']['responses']['200']['content']['application/json']
  >('accounting/v2/providers/partner/payment-requests');

type PaymentRequestResponse = {
  id: string;
  status: string;
  currencyCode: string;
  amount: number;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  paymentProviderName: string;
  description: string;
  userId?: number;
};

export const getPaymentRequestByExternalId = async (externalId: string) =>
  await authenticatedFetch<PaymentRequestResponse>(`accounting/v2/payment-requests/${externalId}`);

type PaymentRequestParamsStripe = {
  amount: number;
  currencyCode: string;
  projectIds?: number[];
  impacterIds?: number[];
  paymentMethod: string;
  email: string;
  firstName?: string;
  lastName?: string;
  contentfulLandingPageId: string;
  emailOptIn: boolean;
  initiator?: string;
  affiliateName?: string;
  categoryIds?: string[];
  language?: string;
  skipAmountValidation?: boolean;
  verificationToken?: string;
  donationProgramId?: string;
  partnerId?: string;
  grantmakerId?: string;
};

type PaymentRequestResponseStripe = {
  id: string;
  requiresAction: boolean;
  paymentIntentClientSecret: string;
  status: string;
};

export const createPaymentRequestForStripe = async ({
  amount,
  currencyCode,
  impacterIds,
  projectIds,
  contentfulLandingPageId,
  paymentMethod,
  email,
  firstName,
  lastName,
  initiator,
  affiliateName,
  emailOptIn,
  categoryIds,
  language,
  skipAmountValidation,
  verificationToken,
  donationProgramId,
  partnerId,
  grantmakerId,
}: PaymentRequestParamsStripe) =>
  await authenticatedFetch<PaymentRequestResponseStripe>(
    'accounting/v2/providers/stripe/payment-requests',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-request-check': verificationToken ?? '',
        ...(skipAmountValidation ? { 'Skip-Amount-Validation': 'true' } : {}),
      },
      body: JSON.stringify({
        amount,
        currencyCode,
        projectIds,
        impacterIds,
        paymentMethod,
        email,
        contentfulLandingPageId,
        firstName,
        lastName,
        emailOptIn,
        initiator,
        affiliateName,
        categoryIds,
        language,
        donationProgramId,
        partnerId,
        grantmakerId,
      }),
    },
  );

type PaymentRequestParamsKlarna = {
  amount: number;
  currencyCode: string;
  language: string;
  country: string;
  initiator: string;
  contentfulLandingPageId: string;
  affiliateName?: string;
  impacterIds?: number[];
  categoryIds?: string[];
  recurring?: boolean;
  skipAmountValidation?: boolean;
  donationProgramId?: string;
  partnerId?: string;
};

export const createPaymentRequestForKlarna = async ({
  amount,
  currencyCode,
  language,
  country,
  contentfulLandingPageId,
  initiator,
  affiliateName,
  impacterIds,
  categoryIds,
  recurring,
  skipAmountValidation,
  donationProgramId,
  partnerId,
}: PaymentRequestParamsKlarna) =>
  await authenticatedFetch<{
    id: string;
    clientToken: string;
    paymentMethodCategories: { identifier: string; name: string }[];
  }>('accounting/v2/providers/klarna/payment-requests', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(skipAmountValidation ? { 'Skip-Amount-Validation': 'true' } : {}),
    },
    body: JSON.stringify({
      amount,
      currencyCode,
      language,
      country,
      initiator,
      contentfulLandingPageId,
      affiliateName,
      impacterIds,
      categoryIds,
      recurring,
      donationProgramId,
      partnerId,
    }),
  });

export const updatePaymentRequestForKlarna = async ({
  paymentRequestId,
  recurring,
  amount,
  contentfulLandingPageId,
}: {
  paymentRequestId: string;
  contentfulLandingPageId: string;
  recurring: boolean;
  amount: number;
}) => {
  await authenticatedFetch(`accounting/v2/providers/klarna/payment-requests/${paymentRequestId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      recurring,
      amount,
      contentfulLandingPageId,
    }),
  });
};

type KlarnaOrderResponse = {
  orderId: string;
  userId: number;
};

export const createKlarnaOrder = async ({
  authorization_token,
  paymentRequestId,
  contentfulLandingPageId,
  amount,
  currencyCode,
}: {
  authorization_token: string;
  paymentRequestId: string;
  contentfulLandingPageId: string;
  amount: number;
  currencyCode: string;
}) =>
  await authenticatedFetch<KlarnaOrderResponse>('accounting/v2/providers/klarna/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      authorization_token,
      paymentRequestId,
      amount,
      contentfulLandingPageId,
      currencyCode,
    }),
  });

type KlarnaSubscriptionParams = {
  authorizationToken: string;
  amount: number;
  currencyCode: string;
  country: string;
  locale: string;
  contentfulLandingPageId: string;
  affiliateName?: string;
  categoryIds: string[];
  donationProgramId?: string;
  partnerId?: string;
};

type CreateKlarnaSubscriptionResponse = {
  subscription: { id: number; currentVersion: number; externalId: string };
  user: { id: number };
};
export const createKlarnaSubscription = async ({
  authorizationToken,
  amount,
  currencyCode,
  country,
  affiliateName,
  categoryIds,
  contentfulLandingPageId,
  locale,
  donationProgramId,
  partnerId,
}: KlarnaSubscriptionParams): Promise<CreateKlarnaSubscriptionResponse> =>
  await authenticatedFetch('accounting/v2/providers/klarna/subscriptions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      authorizationToken,
      amount,
      currencyCode,
      purchaseCountry: country,
      affiliateName,
      categoryIds,
      locale,
      contentfulLandingPageId,
      donationProgramId,
      partnerId,
    }),
  });

type PaymentMethodParamsKlarna = {
  authorizationToken: string;
  currencyCode: string;
  purchaseCountry: string;
  locale: string;
};

export const updatePaymentMethodForKlarna = async ({
  authorizationToken,
  currencyCode,
  locale,
  purchaseCountry,
}: PaymentMethodParamsKlarna) => {
  return authenticatedFetch<{ ok: string; statusText: string }>(
    'accounting/v2/providers/klarna/payment-methods',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authorizationToken,
        currencyCode,
        purchaseCountry,
        locale,
      }),
    },
  );
};

export type Currency = {
  code: string;
  name: string;
  cents: number;
  providers: string[];
};
export const getCurrencies = (providerName?: string) => {
  const query = toQueryString({ providerName });
  return getFromGalaxyCDN<{ result: Currency[] }>(`accounting/v2/currencies${query}`);
};

export type PaymentProvider =
  paths['/v2/providers/']['get']['responses']['200']['content']['application/json']['result'][number];

export type StripePaymentProvider = PaymentProvider & {
  name: 'stripe';
  configuration: {
    publishableKey: string;
    stripeAccountId: string | null;
  };
};

export const getPaymentProviders = ({
  currencyCode,
  grantmakerName,
  categoryId,
  projectId,
}: {
  currencyCode: string;
  grantmakerName?: string;
  categoryId?: string;
  projectId?: number;
}) =>
  getFromGalaxyCDN<
    paths['/v2/providers/']['get']['responses']['200']['content']['application/json']
  >(`accounting/v2/providers`, {
    currencyCode,
    grantmakerName,
    categoryId,
    projectId,
  });

export type AmountSuggestion = {
  amount: number;
  default: boolean;
  currency: { code: string; name: string; symbol: string };
};

export const getAmountSuggestions = ({
  currencyCode,
  configuration = 'default',
}: {
  currencyCode: string;
  configuration?: string;
}) =>
  getFromGalaxyCDN<{ result: AmountSuggestion[] }>(
    `accounting/v2/amount-suggestions/${currencyCode}/${configuration}`,
  );

export enum PaymentMethodType {
  Card = 'CARD',
  DirectDebit = 'DIRECT_DEBIT',
  Wallet = 'WALLET',
}

export type PaymentMethod = {
  id: string;
  default?: boolean;
  created: string;
  card: {
    year: number;
    brand: CardBrand;
    last4: string;
    month: number;
    wallet?: {
      dynamic_last4: string;
      type: WalletType;
    };
  };
  type: PaymentMethodType;
};
export type PaymentMethodsResponse = {
  publicKey: string;
  result: PaymentMethod[];
};

export const getStripePaymentMethods = async () =>
  await authenticatedFetch<PaymentMethodsResponse>(
    `accounting/v2/providers/stripe/payment-methods`,
  );

export const deleteStripePaymentMethod = async (paymentMethodId: string) =>
  await authenticatedFetch(`accounting/v2/providers/stripe/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  });

export const setDefaultStripePaymentMethod = async (paymentMethodId: string) =>
  await authenticatedFetch(`accounting/v2/providers/stripe/payment-methods/${paymentMethodId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      default: true,
    }),
  });

export const addStripePaymentMethod = async (paymentMethodId: string) =>
  authenticatedFetch<{ pendingSetupIntent: { status: string; client_secret: string } | null }>(
    `accounting/v2/providers/stripe/payment-methods`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        id: paymentMethodId,
      }),
    },
  );

export const getKlarnaPaymentMethods = async (): Promise<{
  result: (PaymentMethod & { directDebit?: { maskedNumber?: string } })[];
}> => await authenticatedFetch(`accounting/v2/providers/klarna/payment-methods`);

export enum PaymentProviderName {
  Klarna = 'klarna',
  Partner = 'partner',
  Stripe = 'stripe',

  // Legacy, only used for existing invoices
  Gift = 'gift',
  Swish = 'swish',
}

export enum WalletName {
  apple_pay = 'Apple Pay',
  google_pay = 'Google Pay',
}
export enum WalletType {
  ApplePay = 'apple_pay',
  GooglePay = 'google_pay',
}

export enum CardBrand {
  Visa = 'visa',
  MasterCard = 'mastercard',
  AmericanExpress = 'amex',
}
export enum CardName {
  amex = 'American Express',
  mastercard = 'MasterCard',
  visa = 'Visa',
}

export type PaymentMethodName = WalletName | CardName;

export type Invoice = {
  // FIXME: This type is incomplete. It's missing the `items` property that's found in the response.
  id?: number;
  userId?: number;
  provider?: string;
  createdAt?: string;
  updatedAt?: string;
  amountTotal?: number;
  amountPaid?: number;
  amountPaidInCents?: number;
  subscriptionId?: number;
  userPortfolioId?: string | null;
  userPortfolioVersion?: number | null;
  editedBy?: number | null;
  editedAt?: string | null;
  currencyCode?: string;
  status?: string;
  paymentProviderName?: PaymentProviderName;
  grantmaker?: { id: string; name: string };
  affiliates?: {
    resourceType?: string;
    id?: string;
    title?: string;
  }[];
  providerData?: {
    donationProgramName?: string;
    donationProgramId?: string;
    donationProgramType?: string;
    donationProgramRequiresConfirmation?: boolean;
    partnerId?: string;
    partnerName?: string;
    giftId?: string;
    orderId?: string;
    pendingSetupIntent?: {
      status?: string;
    } | null;
    paymentMethod?: PaymentMethod | null;
    gift?: {
      id?: string;
      period?: number;
      giverName?: string;
    };
  };
};

export const getMyPendingInvoices = () =>
  authenticatedFetch<{ result: Invoice[] }>('accounting/v2/invoices', {
    query: { me: true, pending: true },
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

export type PortfolioItem = {
  id: number;
  impacterId?: number;
  externalImpacterId?: string;
  initiativeId?: string;
  categoryId?: string;
};

export type UserPortfolio = {
  id: string;
  userId: number;
  currentVersion: number;
  items: PortfolioItem[];
};

export const getUserPortfolio = async () =>
  await authenticatedFetch<{
    result: UserPortfolio[];
  }>('accounting/v2/user-portfolios?me=true');

export const addItemToPortfolio = async (item: Omit<PortfolioItem, 'id'>) =>
  await authenticatedFetch(`accounting/v2/user-portfolios/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  });

export const deleteItemFromPortfolio = async (portfolioId: string, itemId: string | number) =>
  await authenticatedFetch(`accounting/v2/user-portfolios/${portfolioId}/items/${itemId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
